import { create } from 'zustand';
import { StyleState, parseStoredStyleData } from '@/components/map/utils/layer-style-helpers';
import { roundForDatabase } from '@/components/map/utils/number-utils';
import { ColumnMetadata, LayerStats, StyleConfig } from '@/components/maplibre/types';
import { geoforgeStore } from '@/components/providers/geoforge-store-provider';
import { updateLayerStyles } from '@/app/actions/layers';
import { LAYER_GEOMETRY_TYPE } from '@/lib/db-constants';
import type { Layer, MapLayer, UserRole } from '@/lib/db-row-types';
import { getBorderDashArray, getBorderLineGapWidth } from '@/utils/layer-sources/style-borders';
import { getUserRoleInWorkspace } from '@/utils/models/workspaces';
import { STYLE_MODE } from './style-constants';
import { createDefaultStyle } from './style-defaults';
import { detectStyleVersion, migrateStyleWithLogging } from './style-migration';
import { StyleV2, safeValidateStyleV2, styleV2ToConfig, toLabelSizeMode } from './style-v2';
import type { LabelSizeMode } from './style-v2';
import { ensureAllGeometrySections, validateStyleV2AgainstStats } from './style-v2-validation';

/**
 * Normalizes a label size/halo value into a LabelSizeMode, rounding the fixed
 * numeric value for DB storage. Zoom-range variants pass through unchanged so
 * they round-trip; their stops are authored values, not rounded.
 */
function toRoundedLabelSizeMode(value: number | LabelSizeMode): LabelSizeMode {
  const mode = toLabelSizeMode(value);
  if (mode.mode === STYLE_MODE.FIXED) {
    return { mode: STYLE_MODE.FIXED, value: roundForDatabase(mode.value) };
  }
  return mode;
}

/**
 * Recursively converts `null` to `undefined` in an object tree, EXCEPT for
 * keys that are explicitly nullable in the StyleV2 schema (e.g., `columnId`, `scheme`, `value`, `customColors`).
 * This handles agent-written styles that use JSON `null` for optional fields,
 * which Zod rejects (it expects `undefined` for missing optional values).
 */
const NULLABLE_KEYS = new Set(['columnId', 'scheme', 'value', 'customColors', 'customIcons']);

export function stripNulls(obj: unknown): unknown {
  if (obj === null) return undefined;
  if (Array.isArray(obj)) return obj.map(stripNulls);
  if (typeof obj === 'object') {
    const result: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(obj as Record<string, unknown>)) {
      if (value === null && NULLABLE_KEYS.has(key)) {
        result[key] = null; // preserve null for explicitly nullable schema fields
      } else {
        const stripped = stripNulls(value);
        if (stripped !== undefined) {
          result[key] = stripped;
        }
      }
    }
    return result;
  }
  return obj;
}

/**
 * Converts StyleState (UI format) to StyleConfig (MapLibre format)
 * Single converter for all layer types
 */
export function styleStateToConfig(styleState: StyleState): StyleConfig {
  return {
    circle: {
      'circle-radius': roundForDatabase(styleState.size),
      'circle-color': styleState.fillColor,
      'circle-opacity': roundForDatabase(styleState.fillOpacity / 100),
      'circle-stroke-color': styleState.strokeColor,
      'circle-stroke-width': roundForDatabase(styleState.strokeWidth),
      'circle-stroke-opacity': 1,
    },
    fill: {
      'fill-color': styleState.polygonFillColor,
      'fill-opacity': roundForDatabase(styleState.polygonFillOpacity / 100),
    },
    line: {
      'line-color':
        styleState.polygonStrokeColor || styleState.lineColor || styleState.polygonFillColor,
      'line-width': roundForDatabase(styleState.polygonStrokeWidth ?? styleState.lineWidth ?? 2),
      'line-opacity': roundForDatabase(
        (styleState.polygonStrokeOpacity ?? styleState.lineOpacity ?? 90) / 100
      ),
      'line-dasharray': getBorderDashArray(styleState.borderType) || [],
      'line-gap-width': getBorderLineGapWidth(styleState.borderType),
    },
    colorLogic: {
      colorLogicColumn: styleState.colorLogicColumn,
      colorLogicScheme: styleState.colorLogicScheme,
      colorLogicType: styleState.colorLogicType,
      colorLogicEnabled: styleState.colorLogicEnabled,
    },
    sizeLogic: {
      sizeBy: styleState.sizeLogic.sizeBy,
      minSize: roundForDatabase(styleState.sizeLogic.minSize),
      maxSize: roundForDatabase(styleState.sizeLogic.maxSize),
    },
    labelLogic: {
      labelBy: styleState.labelLogic.labelBy,
      size: toLabelSizeMode(styleState.labelLogic.size),
      color: styleState.labelLogic.color,
      haloColor: styleState.labelLogic.haloColor,
      haloWidth: toRoundedLabelSizeMode(styleState.labelLogic.haloWidth),
      fontStyle: styleState.labelLogic.fontStyle,
    },
  };
}

/**
 * Triggers async migration and persistence of v1 styles to v2
 * Does not block rendering - happens in background
 */
async function triggerStyleMigrationAndPersist(
  mapLayerId: number,
  v1Style: unknown,
  layer?: Layer
): Promise<void> {
  try {
    const migrationResult = await migrateStyleWithLogging(v1Style, layer);

    if (!migrationResult.success || !migrationResult.data) {
      console.error('Migration failed, will not persist:', migrationResult.error);
      return;
    }

    const v2Style = migrationResult.data;

    const validation = safeValidateStyleV2(v2Style);
    if (!validation.success) {
      console.error('Migrated style failed validation:', validation.error);
      return;
    }

    // Dynamically import to avoid circular dependencies
    const { updateLayerStyles } = await import('@/app/actions/layers');
    const result = await updateLayerStyles(mapLayerId, v2Style);

    if (result.success) {
      console.log(`Successfully migrated and persisted style for map_layer ${mapLayerId}`);
    } else {
      console.error(`Failed to persist migrated style: ${result.error}`);
    }
  } catch (error) {
    console.error('Error during style migration and persistence:', error);
  }
}

/**
 * Triggers async migration of column names to column IDs and persistence
 * Checks columnId fields that are strings (column names) and migrates them to actual IDs
 * Persists column names for backwards compatibility
 * Does not block rendering - happens in background
 */
async function triggerColumnsMigrationAndPersist(
  mapLayerId?: number,
  storedStyleData?: unknown,
  columnMetadata?: ColumnMetadata[]
): Promise<void | StyleV2> {
  if (!mapLayerId || !storedStyleData || !columnMetadata) {
    console.warn('Missing parameters for column migration:', {
      mapLayerId,
      storedStyleData,
      columnMetadata,
    });
    return;
  }

  try {
    const migratedStyle = migrateColumnNamesToIds(storedStyleData, columnMetadata);

    const validation = safeValidateStyleV2(migratedStyle);
    if (!validation.success) {
      console.error('Migrated style failed validation:', validation.error, mapLayerId);
      return;
    }

    // Dynamically import to avoid circular dependencies
    const { updateLayerStyles } = await import('@/app/actions/layers');
    const result = await updateLayerStyles(mapLayerId, migratedStyle);

    if (result.success) {
      console.log(
        `Successfully migrated and persisted column names to IDs for map_layer ${mapLayerId}`
      );

      return migratedStyle as StyleV2;
    } else {
      console.error(`Failed to persist migrated columns: ${result.error}`);
    }
  } catch (error) {
    console.error('Error during column migration and persistence:', error);
  }
}

/**
 * Recursively migrates column names to column IDs throughout the style data
 * Preserves column names in columnName field for backwards compatibility
 */
function migrateColumnNamesToIds(data: unknown, columnMetadata: ColumnMetadata[]): unknown {
  if (data === null || data === undefined) return data;

  if (typeof data !== 'object') return data;

  if (Array.isArray(data)) {
    return data.map(item => migrateColumnNamesToIds(item, columnMetadata));
  }

  const obj = data as Record<string, unknown>;
  const migrated: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(obj)) {
    if (key === 'columnId') {
      if (typeof value === 'string') {
        const column = columnMetadata.find(col => col.column_name === value);

        if (column && column.column_id !== null) {
          migrated['columnId'] = column.column_id;
          migrated['columnName'] = value;
        } else {
          console.warn(`Column name "${value}" not found in metadata, setting columnId to null`);
          migrated['columnId'] = null;
          migrated['columnName'] = value;
        }
      } else if (typeof value === 'number' || value === null) {
        // Already migrated (number) or explicitly null, keep as-is
        migrated[key] = value;
      } else {
        // Other types, keep as-is
        migrated[key] = value;
      }
    } else if (typeof value === 'object') {
      migrated[key] = migrateColumnNamesToIds(value, columnMetadata);
    } else {
      migrated[key] = value;
    }
  }

  return migrated;
}

export const useColumnMigrationStatus = create<{
  status: 'idle' | 'migrating' | 'migrated';
}>()(() => ({
  status: 'idle',
}));

/**
 * Validates a StyleV2 against LayerStats and returns a sanitized style
 * Always ensures all geometry sections exist
 * If stats are available, also validates column references
 * Persists validated style to database if user has permission
 */
async function validateStyleAgainstLayerStats(
  style: StyleV2,
  stats: LayerStats | undefined,
  layer?: Layer,
  mapLayer?: MapLayer,
  columnMetadata?: ColumnMetadata[]
): Promise<StyleV2> {
  const result =
    stats && stats.fields && stats.fields.length > 0
      ? validateStyleV2AgainstStats(style, stats, columnMetadata)
      : ensureAllGeometrySections(style);

  if (result.wasModified) {
    console.warn(
      `Style validation modified for layer "${layer?.title}":`,
      result.modifications.map(m => `${m.property}: ${m.reason}`)
    );

    if (mapLayer && layer) {
      const userCanModify = await canModify(layer);
      if (userCanModify) {
        const persistResult = await updateLayerStyles(mapLayer.id, result.validatedStyle);
        if (persistResult.success) {
          console.log(`Persisted validated style for layer "${layer.title}" after data change`);
          try {
            geoforgeStore.getState().updateMapLayer({
              ...mapLayer,
              mapbox_style_data: result.validatedStyle,
            });
          } catch (e) {
            console.error('Failed to update store with validated style:', e);
          }
        } else {
          console.error(`Failed to persist validated style: ${persistResult.error}`);
        }
      }
    }
  }

  return result.validatedStyle;
}

/**
 * Single entry point for getting layer styles
 * Handles both v1 and v2 formats, triggers migration for v1
 */
export async function getLayerStyle(
  mapLayer: MapLayer | undefined,
  layer: Layer,
  columnMetadata?: ColumnMetadata[]
): Promise<StyleConfig> {
  if (!mapLayer?.mapbox_style_data) {
    return createDefaultStyle();
  }

  try {
    const rawStyleData =
      typeof mapLayer.mapbox_style_data === 'string'
        ? JSON.parse(mapLayer.mapbox_style_data)
        : mapLayer.mapbox_style_data;

    // Agent backend may write `null` for optional fields. Zod accepts `undefined` but not `null`.
    // Strip nulls recursively so Zod treats them as missing (optional) fields.
    const storedStyleData = stripNulls(rawStyleData);

    const version = detectStyleVersion(storedStyleData);

    // If v1, trigger migration in background (don't block rendering)
    if (version === 'v1') {
      console.log(`Detected v1 style for map_layer ${mapLayer.id}, triggering migration`);
      triggerStyleMigrationAndPersist(mapLayer.id, storedStyleData, layer).catch(err => {
        console.error('Migration background task failed:', err);
      });

      const styleState = parseStoredStyleData(storedStyleData);
      return styleStateToConfig(styleState);
    }

    const validation = safeValidateStyleV2(storedStyleData);

    if (validation.success && validation.data) {
      const geometryTypes = new Set([layer.geometry_type || LAYER_GEOMETRY_TYPE.POLYGON]);
      const stats = layer.stats as unknown as LayerStats | undefined;
      const styleToUse = await validateStyleAgainstLayerStats(
        validation.data,
        stats,
        layer,
        mapLayer,
        columnMetadata
      );

      return styleV2ToConfig({
        v2Style: styleToUse,
        columnMetadata,
        stats,
        geometryTypes,
      });
    }

    if (validation.error) {
      const hasColumnIdIssues = validation.error.issues.some(issue => {
        if (issue.code === 'invalid_union') {
          return issue.unionErrors.some(ue =>
            ue.issues.some(issue => issue.path.includes('columnId'))
          );
        }
        return issue.path.includes('columnId');
      });

      const shouldMigrateColumnNamesToIds =
        hasColumnIdIssues && columnMetadata && columnMetadata.length > 0;

      if (shouldMigrateColumnNamesToIds) {
        const canPerformMigration = await canModify(layer);

        if (!canPerformMigration) {
          console.warn(
            'Viewer user cannot migrate column names to IDs, falling back to v1 parsing'
          );
          const styleState = parseStoredStyleData(storedStyleData);
          return styleStateToConfig(styleState);
        }

        useColumnMigrationStatus.setState({ status: 'migrating' });

        const migratedStyle = await triggerColumnsMigrationAndPersist(
          mapLayer.id,
          storedStyleData,
          columnMetadata
        );

        if (migratedStyle) {
          const migratedStyleValidation = safeValidateStyleV2(migratedStyle);
          if (migratedStyleValidation.success) {
            useColumnMigrationStatus.setState({ status: 'migrated' });
            console.log(`Migrated column names to IDs successfully for ${layer?.title}`);
            const geometryTypes = new Set([layer.geometry_type || LAYER_GEOMETRY_TYPE.POLYGON]);
            const stats = layer.stats as unknown as LayerStats | undefined;

            const styleToUse = await validateStyleAgainstLayerStats(
              migratedStyle,
              stats,
              layer,
              mapLayer,
              columnMetadata
            );

            return styleV2ToConfig({
              v2Style: styleToUse,
              columnMetadata,
              stats,
              geometryTypes,
            });
          }
        }

        // Fallback to v1 parsing if v2 validation fails
        console.warn(
          `V2 validation failed for map_layer ${mapLayer.id} - ${layer?.title}, falling back to v1 parsing`,
          { validationError: validation.error, storedStyleData }
        );
        const styleState = parseStoredStyleData(storedStyleData);
        useColumnMigrationStatus.setState({ status: 'idle' });
        return styleStateToConfig(styleState);
      }
    }

    // Fallback to v1 parsing if v2 validation fails
    console.warn(
      `V2 validation failed for map_layer ${mapLayer.id} - ${layer?.title}, falling back to v1 parsing`,
      { validationError: validation.error, storedStyleData }
    );
    const styleState = parseStoredStyleData(storedStyleData);
    return styleStateToConfig(styleState);
  } catch (error) {
    console.warn(
      `Failed to parse stored layer style for map_layer ${mapLayer.id}, using defaults:`,
      error
    );
    return createDefaultStyle();
  }
}

const permissionCache = new Map<string, { canModify: boolean; timestamp: number }>();
const CACHE_TTL = 30000;

const canModify = async (layer?: Layer): Promise<boolean> => {
  if (!layer?.workspace_id) return false;

  const cacheKey = `${layer.workspace_id}`;
  const cached = permissionCache.get(cacheKey);
  const now = Date.now();

  if (cached && now - cached.timestamp < CACHE_TTL) {
    return cached.canModify;
  }

  const userRole = await getUserRoleInWorkspace(layer.workspace_id);

  if (!userRole) return false;

  const canModify = roleCanModifyMapLayerStyle(userRole);
  permissionCache.set(cacheKey, { canModify, timestamp: now });

  return canModify;
};

// Positive allow-list: analysts (and any future role) must not get map-layer
// style editing just by not being a viewer.
export const roleCanModifyMapLayerStyle = (role: UserRole): boolean =>
  role === 'owner' || role === 'editor';
